import React, { useState, useEffect, useRef } from 'react';
import { AxiosError } from 'axios';
import { AdminProductDetailDto, AdminCreateProductDto, AdminUpdateProductDto, ProductCondition, ProductStatus, AdminUserListDto } from '../../types';
import { SpecificationDto, CategoryDto } from '../../types';
import { getAllAdminCategories, getAdminSpecificationsForCategories, uploadAdminProductImages } from '../../services/api.admin';
import { ValidatedInput } from './ValidatedInput';
import { scrollToFirstErrorField } from '../../utils/scrollToFirstError';
import { parseAxiosValidationErrors } from '../../utils/productValidation';
import { ValidationError } from './ValidationError';
import AdminModalShell from './AdminModalShell';

interface ProductModalProps {
    isOpen: boolean;
    onClose: () => void;
    isCreating: boolean;
    selectedProduct: AdminProductDetailDto | null;
    createFormData: AdminCreateProductDto;
    setCreateFormData: React.Dispatch<React.SetStateAction<AdminCreateProductDto>>;
    updateFormData: AdminUpdateProductDto;
    setUpdateFormData: React.Dispatch<React.SetStateAction<AdminUpdateProductDto>>;
    handleSaveProduct: (payload: AdminCreateProductDto | AdminUpdateProductDto) => Promise<void>;
    users: AdminUserListDto[];
    isLoading: boolean;
    additionalFields: string[];
}

const ProductModal: React.FC<ProductModalProps> = ({
    isOpen, onClose, isCreating, selectedProduct,
    createFormData, setCreateFormData,
    updateFormData, setUpdateFormData,
    handleSaveProduct, users, isLoading, additionalFields: _additionalFields,
}) => {
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [categories, setCategories] = useState<CategoryDto[]>([]);
    const [categorySpecifications, setCategorySpecifications] = useState<{ [key: string]: SpecificationDto[] }>({});
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

    // === NEW: Multi-image state ===
    const [uploadedImages, setUploadedImages] = useState<{ id: string; file: File; preview: string }[]>([]);
    const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const cats = await getAllAdminCategories();
                setCategories(cats);
            } catch (err) {
                console.error('Failed to load categories');
            }
        };
        fetchCategories();
    }, []);

    // Load existing images when editing
    useEffect(() => {
        if (!isCreating && selectedProduct) {
            setExistingImageUrls(selectedProduct.imageUrls ?? []);
            setUploadedImages([]);
            setSelectedCategories(selectedProduct.categories ?? []);
        }
    }, [selectedProduct, isCreating]);

    useEffect(() => {
        const fetchSpecs = async () => {
            const missing = selectedCategories.filter(cat => !categorySpecifications[cat]);
            const newSpecsFromFetch: { [key: string]: SpecificationDto[] } = {};
            if (missing.length > 0) {
                const promises = missing.map(async (cat) => {
                    try {
                        const specList = await getAdminSpecificationsForCategories([cat]);
                        return { cat, specList: specList || [] };
                    } catch (err) {
                        console.error(`Failed to load specifications for ${cat}`);
                        return { cat, specList: [] };
                    }
                });
                const results = await Promise.all(promises);
                results.forEach(({ cat, specList }) => {
                    newSpecsFromFetch[cat] = specList;
                });
            }
            setCategorySpecifications((prev) => {
                const newCategorySpecs = { ...prev, ...newSpecsFromFetch };
                let hasChanges = Object.keys(newSpecsFromFetch).length > 0;
                Object.keys(newCategorySpecs).forEach((cat) => {
                    if (!selectedCategories.includes(cat)) {
                        delete newCategorySpecs[cat];
                        hasChanges = true;
                    }
                });
                return hasChanges ? newCategorySpecs : prev;
            });
        };
        fetchSpecs();
    }, [selectedCategories]);
    useEffect(() => {
        if (isCreating) {
            setCreateFormData((prev) => {
                const newSpecs = { ... (prev.specifications ?? {}) };
                let specChanged = false;
                Object.keys(newSpecs).forEach((cat) => {
                    if (!selectedCategories.includes(cat)) {
                        delete newSpecs[cat];
                        specChanged = true;
                    }
                });
                return {
                    ...prev,
                    categories: selectedCategories,
                    specifications: specChanged ? newSpecs : prev.specifications,
                };
            });
        } else {
            setUpdateFormData((prev) => {
                const newSpecs = { ... (prev.specifications ?? {}) };
                let specChanged = false;
                Object.keys(newSpecs).forEach((cat) => {
                    if (!selectedCategories.includes(cat)) {
                        delete newSpecs[cat];
                        specChanged = true;
                    }
                });
                return {
                    ...prev,
                    categories: selectedCategories,
                    specifications: specChanged ? newSpecs : prev.specifications,
                };
            });
        }
    }, [selectedCategories, isCreating, setCreateFormData, setUpdateFormData]);
    useEffect(() => {
        if (!isCreating && selectedProduct) {
            setSelectedCategories(selectedProduct.categories ?? []);
            setUpdateFormData((prev) => ({
                ...prev,
                categories: selectedProduct.categories ?? prev.categories,
                specifications: selectedProduct.specifications ?? prev.specifications,
            }));
            // No need for separate loadCategorySpecifications call; the fetchSpecs effect will handle loading after selectedCategories is set.
        }
    }, [selectedProduct, isCreating, setUpdateFormData]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        const newFiles = Array.from(e.target.files);
        const total = uploadedImages.length + existingImageUrls.length + newFiles.length;
        if (total > 5) {
            setErrors(prev => ({ ...prev, Images: 'Maximum 5 images allowed' }));
            return;
        }
        const newItems = newFiles.map(file => ({
            id: crypto.randomUUID(),
            file,
            preview: URL.createObjectURL(file)
        }));
        setUploadedImages(prev => [...prev, ...newItems]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeImage = (idOrUrl: string) => {
        setUploadedImages(prev => {
            const image = prev.find(img => img.id === idOrUrl);
            if (image) URL.revokeObjectURL(image.preview);
            return prev.filter(img => img.id !== idOrUrl);
        });
        setExistingImageUrls(prev => prev.filter(url => url !== idOrUrl));
        setErrors(prev => {
            const n = { ...prev };
            delete n.Images;
            return n;
        });
    };

    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};
        const formData = isCreating ? createFormData : updateFormData;
        if (!formData.title?.trim()) newErrors.Title = 'Title is required';
        const price = formData.price ?? 0; // Handle optional in update
        if (price <= 0) newErrors.Price = 'Price must be positive';
        const quantity = formData.quantityAvailable ?? 0;
        if (quantity < 1) newErrors.QuantityAvailable = 'Quantity must be at least 1';
        selectedCategories.forEach(cat => {
            const specsForCat = categorySpecifications[cat] || [];
            specsForCat.forEach(spec => {
                const catSpecs = formData.specifications?.[cat] || {};
                if (spec.isRequired && !catSpecs[spec.name]?.trim()) {
                    newErrors[`${cat}-${spec.name}`] = `${spec.name} for ${cat} is required`;
                }
            });
        });
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleSubmit = async () => {
        setErrors({});
        if (!validateForm()) {
            scrollToFirstErrorField();
            return;
        }

        try {
            // 1. Upload any new images
            let finalImageUrls = [...existingImageUrls];

            if (uploadedImages.length > 0) {
                const newUrls = await uploadAdminProductImages(uploadedImages.map(img => img.file));
                finalImageUrls = [...finalImageUrls, ...newUrls];
            }

            // 2. Create final payload with the correct imageUrls
            const finalPayload = isCreating
                ? { ...createFormData, imageUrls: finalImageUrls }
                : { ...updateFormData, imageUrls: finalImageUrls };

            // 3. Pass the final payload directly to the parent
            await handleSaveProduct(finalPayload);

            // Clean up previews
            uploadedImages.forEach(img => URL.revokeObjectURL(img.preview));
            setUploadedImages([]);
            setExistingImageUrls(finalImageUrls); // optional: keep them in UI
        } catch (err: unknown) {
            if (err instanceof AxiosError && err.response) {
                const backendErrors = parseAxiosValidationErrors(err);
                setErrors(backendErrors);
                scrollToFirstErrorField(backendErrors);
            } else if (err instanceof Error) {
                setErrors({ General: 'Save failed: ' + err.message });
            } else {
                setErrors({ General: 'Save failed: Unknown error' });
            }
        }
    };

    const formatDate = (isoString?: string): string => {
        if (!isoString) return 'N/A';
        const date = new Date(isoString);
        return date.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    };
    const handleSpecChange = (cat: string, name: string, value: string | boolean) => {
        const formData = isCreating ? createFormData : updateFormData;
        const nestedSpecs = { ...(formData.specifications || {}) };
        nestedSpecs[cat] = { ...(nestedSpecs[cat] || {}), [name]: value.toString() };
        if (isCreating) {
            setCreateFormData(prev => ({ ...prev, specifications: nestedSpecs }));
        } else {
            setUpdateFormData(prev => ({ ...prev, specifications: nestedSpecs }));
        }
        // clear per-spec error on edit
        setErrors(prev => {
            const k = `${cat}-${name}`;
            if (!prev[k]) return prev;
            const n = { ...prev };
            delete n[k];
            return n;
        });
    };
    const renderSpecInput = (cat: string, spec: SpecificationDto) => {
        const formData = isCreating ? createFormData : updateFormData;
        const catSpecs = formData.specifications?.[cat] || {};
        const value = catSpecs[spec.name] ?? '';
        const isDisabled = !isCreating && isLoading;
        const specKey = `${cat}-${spec.name}`;
        const hasErr = !!errors[specKey];
        const className = `${isDisabled ? 'opacity-50 cursor-not-allowed' : ''} w-full border rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${hasErr ? 'border-red-300 bg-red-50/30' : 'border-gray-300'}`;
        switch (spec.type) {
            case 'Boolean': {
                return (
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            checked={value === 'true'}
                            onChange={(e) => handleSpecChange(cat, spec.name, e.target.checked)}
                            disabled={isDisabled}
                            className={isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                        />
                        {isDisabled && (
                            <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                        )}
                    </div>
                );
            }
            case 'Enum': {
                const options = spec.options?.split(',') ?? [];
                return (
                    <div className="flex items-center">
                        <select
                            value={value}
                            onChange={(e) => handleSpecChange(cat, spec.name, e.target.value)}
                            disabled={isDisabled}
                            className={className}
                        >
                            {options.map(opt => <option key={opt} value={opt.trim()}>{opt.trim()}</option>)}
                        </select>
                        {isDisabled && (
                            <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                        )}
                    </div>
                );
            }
            case 'Integer':
            case 'Decimal': {
                return (
                    <div className="flex items-center">
                        <input
                            type="number"
                            value={value}
                            onChange={(e) => handleSpecChange(cat, spec.name, e.target.value)}
                            disabled={isDisabled}
                            className={className}
                        />
                        {isDisabled && (
                            <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                        )}
                    </div>
                );
            }
            case 'Date': {
                return (
                    <div className="flex items-center">
                        <input
                            type="date"
                            value={value}
                            onChange={(e) => handleSpecChange(cat, spec.name, e.target.value)}
                            disabled={isDisabled}
                            className={className}
                        />
                        {isDisabled && (
                            <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                        )}
                    </div>
                );
            }
            default: { // String
                return (
                    <div className="flex items-center">
                        <input
                            type="text"
                            value={value}
                            onChange={(e) => handleSpecChange(cat, spec.name, e.target.value)}
                            disabled={isDisabled}
                            className={className}
                        />
                        {isDisabled && (
                            <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                        )}
                    </div>
                );
            }
        }
    };
    const handleCategoryToggle = (catName: string) => {
        setSelectedCategories(prev =>
            prev.includes(catName)
                ? prev.filter(c => c !== catName)
                : [...prev, catName]
        );
    };
    const isSaveDisabled = isLoading || (!isCreating && selectedProduct === null);
    const isFieldDisabled = !isCreating && isLoading;
    const getFieldClass = (fieldKey?: string) => {
        const hasErr = fieldKey ? !!errors[fieldKey] : false;
        return `w-full border rounded-md px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 shadow-sm ${hasErr ? 'border-red-300 bg-red-50/30' : 'border-gray-300'} ${isFieldDisabled ? 'opacity-50 cursor-not-allowed' : ''}`;
    };
    const fieldClassName = getFieldClass(); // default no error for legacy uses

    if (!isOpen) return null;

    return (
        <AdminModalShell
            title={isCreating ? 'Create Product' : 'Edit Product'}
            onClose={onClose}
            size="2xl"
        >
            {/* === NEW MULTI-IMAGE SECTION === */}
            <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">Product Images (max 5)</label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-500">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageChange}
                        className="hidden"
                        id="admin-image-upload"
                    />
                    <label htmlFor="admin-image-upload" className="cursor-pointer block">
                        <p className="text-gray-500">Click to upload images</p>
                    </label>
                </div>

                {(existingImageUrls.length > 0 || uploadedImages.length > 0) && (
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5 mt-6">
                        {existingImageUrls.map((url, index) => (
                            <div key={`existing-${index}`} className="relative group">
                                <img src={url} alt={`Existing ${index + 1}`} className="w-full h-24 object-cover rounded-lg border" />
                                <button type="button" onClick={() => removeImage(url)} className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">✕</button>
                            </div>
                        ))}
                        {uploadedImages.map((img) => (
                            <div key={img.id} className="relative group">
                                <img src={img.preview} alt="Preview" className="w-full h-24 object-cover rounded-lg border" />
                                <button type="button" onClick={() => removeImage(img.id)} className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">✕</button>
                            </div>
                        ))}
                    </div>
                )}
                <ValidationError error={errors.Images} />
            </div>
            <ValidationError error={errors.General} />
            <div className="grid grid-cols-1 gap-4">
                {/* Seller */}
                <div data-error-field="SellerId">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Seller</label>
                    {isCreating ? (
                        <select
                            value={createFormData.sellerId}
                            onChange={(e) => {
                                setCreateFormData({ ...createFormData, sellerId: e.target.value });
                                setErrors(prev => { const n = { ...prev }; delete n.SellerId; return n; });
                            }}
                            className={getFieldClass('SellerId')}
                        >
                            <option value="">Select a seller</option>
                            {users.map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.email} ({user.userName})
                                </option>
                            ))}
                        </select>
                    ) : (
                        <div className="flex items-center">
                            <input
                                value={selectedProduct?.sellerId ?? ''}
                                disabled={isFieldDisabled}
                                className={getFieldClass('SellerId')}
                            />
                            {isFieldDisabled && (
                                <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            )}
                        </div>
                    )}
                    <ValidationError error={errors.SellerId} />
                </div>
                {/* Title */}
                <div data-error-field="Title">
                    <label className="block text-sm font-medium text-gray-700 mb-1"/>
                    <div className="flex items-center">
                        <ValidatedInput
                            label="Title"
                            name="title"
                            type="text"
                            value={isCreating ? createFormData.title : updateFormData.title ?? selectedProduct?.title ?? ''}
                            onChange={(e) => isCreating ? setCreateFormData({ ...createFormData, title: e.target.value }) : setUpdateFormData({ ...updateFormData, title: e.target.value })}
                            className={fieldClassName}
                            disabled={isFieldDisabled}
                            error={errors.Title}
                        />
                        {isFieldDisabled && (
                            <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                        )}
                    </div>
                </div>
                {/* Price */}
                <div data-error-field="Price">
                    <label className="block text-sm font-medium text-gray-700 mb-1" />
                    <div className="flex items-center">
                        <ValidatedInput
                            label="Price"
                            name="price"
                            type="number"
                            clampMin={0} // Clamps to 0+
                            step="0.01" // For decimals
                            value={isCreating ? createFormData.price : updateFormData.price ?? selectedProduct?.price ?? 0}
                            onChange={(e) => {
                                const value = parseFloat(e.target.value);
                                if (isCreating) setCreateFormData({ ...createFormData, price: value });
                                else setUpdateFormData({ ...updateFormData, price: value });
                            }}
                            disabled={isFieldDisabled}
                            className={fieldClassName}
                            error={errors.Price}
                        />
                        {isFieldDisabled && (
                            <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                        )}
                    </div>
                </div>
                {/* Description */}
                <div data-error-field="Description">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <div className="flex items-center">
                        <textarea
                            value={isCreating ? createFormData.description : updateFormData.description ?? selectedProduct?.description ?? ''}
                            onChange={(e) => {
                                const v = e.target.value;
                                if (isCreating) setCreateFormData({ ...createFormData, description: v });
                                else setUpdateFormData({ ...updateFormData, description: v });
                                setErrors(prev => { const n = { ...prev }; delete n.Description; return n; });
                            }}
                            className={getFieldClass('Description')}
                            disabled={isFieldDisabled}
                        />
                        {isFieldDisabled && (
                            <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                        )}
                    </div>
                    <ValidationError error={errors.Description} />
                </div>
                {/* Condition */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                    <div className="flex items-center">
                        <select
                            value={isCreating ? createFormData.condition : updateFormData.condition ?? ProductCondition.LikeNew}
                            onChange={(e) => {
                                const value = e.target.value as ProductCondition;
                                isCreating ? setCreateFormData({ ...createFormData, condition: value }) : setUpdateFormData({ ...updateFormData, condition: value });
                                setErrors(prev => { const n = { ...prev }; delete n.Condition; return n; });
                            }}
                            className={getFieldClass('Condition')}
                            disabled={isFieldDisabled}
                        >
                            {Object.values(ProductCondition).map(cond => <option key={cond} value={cond}>{cond}</option>)}
                        </select>
                        {isFieldDisabled && (
                            <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                        )}
                    </div>
                    <ValidationError error={errors.Condition} />
                </div>
                {/* Quantity Available */}
                <div data-error-field="QuantityAvailable">
                    <label className="block text-sm font-medium text-gray-700 mb-1" />
                    <div className="flex items-center">
                        <ValidatedInput
                            label="Quantity Available"
                            name="quantityAvailable"
                            type="number"
                            clampMin={1}
                            step="1" // Integers
                            value={isCreating ? createFormData.quantityAvailable : updateFormData.quantityAvailable ?? 1}
                            onChange={(e) => {
                                const value = parseInt(e.target.value, 10);
                                if (isCreating) setCreateFormData({ ...createFormData, quantityAvailable: value });
                                else setUpdateFormData({ ...updateFormData, quantityAvailable: value });
                            }}
                            disabled={isFieldDisabled}
                            className={fieldClassName}
                            error={errors.QuantityAvailable}
                        />
                        {isFieldDisabled && (
                            <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                        )}
                    </div>
                </div>
                {/* Categories */}
                <div data-error-field="Categories">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Categories</label>
                    <div className="flex flex-wrap gap-2 mb-2">
                        {selectedCategories.map(cat => (
                            <span key={cat} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center">
                                {cat}
                                <button type="button" onClick={() => handleCategoryToggle(cat)} className="ml-2 text-blue-600 hover:text-blue-800" disabled={isFieldDisabled}>✕</button>
                            </span>
                        ))}
                        {selectedCategories.length === 0 && <p className="text-gray-500 text-sm">No categories selected yet.</p>}
                    </div>
                    <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md p-2">
                        {categories.map(cat => (
                            <div key={cat.id} className="flex items-center mb-2">
                                <input type="checkbox" id={`cat-${cat.id}`} checked={selectedCategories.includes(cat.name)} onChange={() => handleCategoryToggle(cat.name)} className="mr-2" disabled={isFieldDisabled} />
                                <label htmlFor={`cat-${cat.id}`} className={`text-sm text-gray-900 ${isFieldDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}>{cat.name}</label>
                            </div>
                        ))}
                    </div>
                    {isFieldDisabled && (
                        <div className="mt-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                    )}
                    <ValidationError error={errors.Categories} />
                </div>
                {/* Dynamic Specifications grouped per category */}
                {selectedCategories.length > 0 && (
                    <h3 className="mt-4 text-md font-bold text-gray-800">Specifications</h3>
                )}
                {selectedCategories.map(cat => (
                    <div key={cat}>
                        <h4 className="mt-2 text-sm font-semibold text-gray-700">{cat} Specifications</h4>
                        {categorySpecifications[cat] ? (
                            categorySpecifications[cat].map(spec => (
                                <div key={spec.id} data-error-field={`${cat}-${spec.name}`}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">{spec.name} {spec.isRequired ? '(Required)' : ''}</label>
                                    {renderSpecInput(cat, spec)}
                                    <ValidationError error={errors[`${cat}-${spec.name}`]} />
                                </div>
                            ))
                        ) : (
                            <p className="text-gray-500 text-sm">Loading specifications...</p>
                        )}
                    </div>
                ))}
                <div data-error-field="Tags">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                    <div className="flex items-center">
                        <input
                            value={isCreating ? createFormData.tags?.join(', ') ?? '' : updateFormData.tags?.join(', ') ?? ''}
                            onChange={(e) => {
                                const value = e.target.value.split(',').map(tag => tag.trim());
                                isCreating ? setCreateFormData({ ...createFormData, tags: value }) : setUpdateFormData({ ...updateFormData, tags: value });
                                setErrors(prev => { const n = { ...prev }; delete n.Tags; return n; });
                            }}
                            className={getFieldClass('Tags')}
                            disabled={isFieldDisabled}
                        />
                        {isFieldDisabled && (
                            <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                        )}
                    </div>
                    <ValidationError error={errors.Tags} />
                </div>
                {!isCreating && (
                    <div data-error-field="Status">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                        <div className="flex items-center">
                            <select
                                value={updateFormData.status ?? ProductStatus.PendingReview}
                                onChange={(e) => {
                                    const value = e.target.value as ProductStatus;
                                    setUpdateFormData({ ...updateFormData, status: value });
                                }}
                                className={fieldClassName}
                                disabled={isFieldDisabled}
                            >
                                {Object.values(ProductStatus).map(stat => <option key={stat} value={stat}>{stat}</option>)}
                            </select>
                            {isFieldDisabled && (
                                <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            )}
                        </div>
                        <ValidationError error={errors.Status} />
                    </div>
                )}
                {!isCreating && (
                    <div data-error-field="IsSold">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Is Sold</label>
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                checked={updateFormData.isSold ?? selectedProduct?.isSold ?? false}
                                onChange={(e) => setUpdateFormData({ ...updateFormData, isSold: e.target.checked })}
                                disabled={isFieldDisabled}
                                className={isFieldDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                            />
                            {isFieldDisabled && (
                                <div className="ml-2 h-4 w-4 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600"></div>
                            )}
                        </div>
                        <ValidationError error={errors.IsSold} />
                    </div>
                )}
            </div>
            {!isCreating && selectedProduct && (
                <>
                    <h3 className="mt-6 text-lg font-bold text-gray-800">Details</h3>
                    {!isLoading ? (
                        <>
                    <p>Created: {formatDate(selectedProduct.createdAt)}</p>
                    <p>Updated: {formatDate(selectedProduct.updatedAt)}</p>
                    <p>Average Rating: {selectedProduct.averageRating.toFixed(1)} ({selectedProduct.reviewCount} reviews)</p>
                            <p>View Count: {selectedProduct.viewCount}</p>
                        </>
                    ) : (
                        <div className="flex items-center justify-center py-4">
                            <div className="h-6 w-6 animate-spin rounded-full border-t-2 border-b-2 border-indigo-600 mr-2"></div>
                            <p className="text-gray-600">Loading additional details...</p>
                        </div>
                    )}
                </>
            )}
            <div className="mt-6 flex justify-end">
                <button
                    onClick={handleSubmit}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium mr-3"
                    disabled={isSaveDisabled}>Save</button>
                <button onClick={onClose} className="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400 transition-colors font-medium">Cancel</button>
            </div>
        </AdminModalShell>
    );
};
export default ProductModal;